import { Anchor, Box, Text } from "@mantine/core";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownContentProps {
  value: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  dimmed?: boolean;
}

const VIDEO_HOSTS: { pattern: RegExp; embed: (m: RegExpMatchArray) => string }[] = [
  {
    pattern: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/,
    embed: (m) => `https://www.youtube.com/embed/${m[1]}`,
  },
  {
    pattern: /streamable\.com\/([a-z0-9]+)/i,
    embed: (m) => `https://streamable.com/e/${m[1]}`,
  },
  {
    pattern: /medal\.tv\/games\/[^/]+\/clips\/([^/?#]+)/i,
    embed: (m) => `https://medal.tv/clip/${m[1]}/embed`,
  },
];

function resolveEmbed(href: string): string | null {
  for (const { pattern, embed } of VIDEO_HOSTS) {
    const m = href.match(pattern);
    if (m) return embed(m);
  }
  return null;
}

function isImageUrl(href: string) {
  return /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(href);
}

export function MarkdownContent({ value, size = "sm", dimmed = false }: MarkdownContentProps) {
  return (
    <Box>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Text size={size} c={dimmed ? "dimmed" : undefined} mb={6} style={{ lineHeight: 1.55 }}>
              {children}
            </Text>
          ),
          li: ({ children }) => (
            <li>
              <Text span size={size} c={dimmed ? "dimmed" : undefined} style={{ lineHeight: 1.55 }}>
                {children}
              </Text>
            </li>
          ),
          ul: ({ children }) => <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>{children}</ol>,
          img: ({ src, alt }) =>
            src ? (
              <img
                src={src}
                alt={alt ?? ""}
                style={{ maxWidth: "100%", borderRadius: 8, display: "block", margin: "8px 0" }}
              />
            ) : null,
          a: ({ href, children }) => {
            const url = href ?? "#";
            const embedSrc = resolveEmbed(url);
            if (embedSrc) {
              return (
                <Box
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingBottom: "56.25%",
                    marginBottom: 8,
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "var(--bg-elev)",
                  }}
                >
                  <iframe
                    src={embedSrc}
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                </Box>
              );
            }
            if (isImageUrl(url)) {
              return (
                <img
                  src={url}
                  alt={typeof children === "string" ? children : "image"}
                  style={{ maxWidth: "100%", borderRadius: 8, display: "block", margin: "8px 0" }}
                />
              );
            }
            return (
              <Anchor href={url} target="_blank" rel="noreferrer" size={size}>
                {children}
              </Anchor>
            );
          },
          code: ({ children }) => (
            <code
              style={{
                background: "var(--bg-elev2)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                padding: "0 4px",
              }}
            >
              {children}
            </code>
          ),
          blockquote: ({ children }) => (
            <Box
              style={{
                borderLeft: "3px solid var(--border-hover)",
                paddingLeft: 12,
                margin: "6px 0",
                opacity: 0.8,
              }}
            >
              {children}
            </Box>
          ),
        }}
      >
        {value}
      </ReactMarkdown>
    </Box>
  );
}
